import { Backdrop, Box, CircularProgress } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { GOOGLE_MAPS_API_KEY, loadScript } from "./GoogleMapsService"
import { useAppSelector } from "../../app/hooks"
import { selectAnalysisData } from "../analysis/AnalysisSlice"
import html2canvas from "html2canvas"

export interface CompMapProps {
  printable?: boolean
  showMap?: boolean
  onMapCompleted?: (image: string) => void
}

function CompMap(props: CompMapProps) {
  const analysisData = useAppSelector(selectAnalysisData)
  const [loadRetryCount, setLoadRetryCount] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const mapBox = useRef<HTMLElement>()
  const mapExportBox = useRef<HTMLElement>()
  const [map, setMap] = useState<any>()
  const [compmarker, setCompMarkers] = useState<any[]>([])
  const [primaryMarker, setPrimaryMarker] = useState<any>()

  if (typeof window !== 'undefined' && !loaded) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=loadedGoogleMaps`,
        document.querySelector('head'),
        'google-maps',
        () => { })
    }

  }

  const addressToGeo = (address: any) => {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ address }, (results: google.maps.GeocoderResult[] | null, status) => {
        if (status === "OK" && results) {
          const location = results[0].geometry.location
          resolve({ lat: location.lat(), lng: location.lng() })
        } else {
          reject("Geocode was unsuccessful due to: " + status)
        }
      })
    })
  }

  useEffect(() => {
    if (loaded) {
      const wgoogle = (window as any).google
      if (wgoogle?.maps?.Map) {
        const initMap = new (window as any).google.maps.Map(mapBox.current, {
          zoom: 12,
          center: { lat: 32.9237, lng: -96.8090 },
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: (window as any).google.maps.MapTypeControlStyle.DEFAULT,
            position: (window as any).google.maps.ControlPosition.TOP_RIGHT,
          }
        })
        setMap(initMap)
        const compMarkers: any[] = []
        analysisData?.comps?.listings?.forEach((listing: any) => {
          const compMarker = new (window as any).google.maps.Marker({
            position: { lat: listing.latitude, lng: listing.longitude },
            map: initMap,
            opacity: 0.7
          })
          compMarker['info'] = new (window as any).google.maps.InfoWindow({
            content: `<h2>${listing.address}</h2>
            <p>Price: $${listing.price}</p>
            <p>Bed: ${listing.bedrooms}, Bath: ${listing.bathrooms}, Sqft: ${listing.squareFootage}</p>
            <p>Listed: ${listing.publishedDate?.substring(0, 10)}</p>`
          })
          compMarker.addListener(`mouseover`, () => { compMarker['info']?.open(initMap, compMarker) })
          compMarker.addListener(`mouseout`, () => { compMarker['info']?.close() })

          compMarkers.push(compMarker)
        })

        addressToGeo(analysisData?.detail?.formattedAddress).then((position) => {
          const primaryMarker = new (window as any).google.maps.Marker({
            map: initMap,
            position,
            icon: {
              url: `/images/map-pin.png`,
              scaledSize: new (window as any).google.maps.Size(64, 64)
            },
          })

          primaryMarker['info'] = new (window as any).google.maps.InfoWindow({
            content: `<h2>${analysisData?.detail?.formattedAddress}</h2>
            <p>Price: $${analysisData?.detail?.lastSalePrice}</p>
            <p>Bed: ${analysisData?.detail?.bedrooms}, Bath: ${analysisData?.detail?.bathrooms}, Sqft: ${analysisData?.detail?.squareFootage}</p>
            <p>Estimated: $${analysisData?.comp} (${analysisData?.confidence})</p>`
          })
          primaryMarker.addListener(`mouseover`, () => { primaryMarker['info'].open(initMap, primaryMarker) })
          primaryMarker.addListener(`mouseout`, () => { primaryMarker['info'].close() })

          const bounds = new (window as any).google.maps.LatLngBounds()
          compMarkers.forEach((compMarker) => bounds.extend(compMarker.getPosition()))
          bounds.extend(primaryMarker.getPosition())
          initMap.fitBounds(bounds)
          setPrimaryMarker(primaryMarker)
        })

        setCompMarkers(compMarkers)

      } else {
        console.log(`oops`)
      }
    } else {
      setTimeout(() => {
        setLoaded(true)
      }, 5000)
    }
    return () => { setMap(undefined) }
  }, [loaded, loadRetryCount, analysisData])

  useEffect(() => {
    setTimeout(() => {
      mapExportBox?.current && html2canvas(mapExportBox.current, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: null
      }).then((canvas) => {
        console.log('Map completed')
        props.onMapCompleted && props.onMapCompleted(canvas.toDataURL('image/png'))
      }).catch((e) => {
        console.log('Error converting map to B64', e)
      })
    }, 1000)
  }, [mapBox.current, primaryMarker, compmarker])

  return (<Box sx={{ pageBreakBefore: 'always' }} ref={mapExportBox}>
    <Box ref={mapBox} sx={{ minHeight: { xs: 250, sm: 500, lg: 700 } }}>
      {!loaded && <CircularProgress />}
    </Box>
  </Box>)
}

export default CompMap